/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingBag,
  Smartphone,
  Watch,
  Home,
  Shirt,
  Footprints,
  Gift,
  Sparkles,
  ArrowRight,
  Search,
  TrendingUp,
  Star,
  Zap,
  Loader2,
  Package,
  ChevronRight,
  X,
  LayoutGrid,
  Layers
} from 'lucide-react';
import { categoryService } from '../api/categories';
import { Category } from '../types';

/* ============================================================
   ANIMATION VARIANTS
   ============================================================ */
const fadeInUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45 } }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

const slideInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

const slideInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.5 } }
};

/* ============================================================
   ICON MAPPING
   ============================================================ */
const getCategoryIcon = (iconName?: string) => {
  const icons: Record<string, React.ReactNode> = {
    'smartphone': <Smartphone className="w-6 h-6" />,
    'shirt': <Shirt className="w-6 h-6" />,
    'footprints': <Footprints className="w-6 h-6" />,
    'home': <Home className="w-6 h-6" />,
    'watch': <Watch className="w-6 h-6" />,
    'gift': <Gift className="w-6 h-6" />,
    'shopping': <ShoppingBag className="w-6 h-6" />,
    'package': <Package className="w-6 h-6" />,
  };
  return icons[iconName || ''] || <LayoutGrid className="w-6 h-6" />;
};

/* ============================================================
   COLOR PALETTE
   ============================================================ */
const getCategoryColor = (index: number) => {
  const colors = [
    { from: 'from-blue-500', to: 'to-cyan-400', bg: 'bg-blue-500', text: 'text-blue-600', border: 'border-blue-200', hover: 'group-hover:shadow-blue-200/50' },
    { from: 'from-pink-500', to: 'to-rose-400', bg: 'bg-pink-500', text: 'text-pink-600', border: 'border-pink-200', hover: 'group-hover:shadow-pink-200/50' },
    { from: 'from-amber-500', to: 'to-orange-400', bg: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200', hover: 'group-hover:shadow-amber-200/50' },
    { from: 'from-emerald-500', to: 'to-teal-400', bg: 'bg-emerald-500', text: 'text-emerald-600', border: 'border-emerald-200', hover: 'group-hover:shadow-emerald-200/50' },
    { from: 'from-violet-500', to: 'to-purple-400', bg: 'bg-violet-500', text: 'text-violet-600', border: 'border-violet-200', hover: 'group-hover:shadow-violet-200/50' },
    { from: 'from-red-500', to: 'to-pink-400', bg: 'bg-red-500', text: 'text-red-600', border: 'border-red-200', hover: 'group-hover:shadow-red-200/50' },
    { from: 'from-indigo-500', to: 'to-blue-400', bg: 'bg-indigo-500', text: 'text-indigo-600', border: 'border-indigo-200', hover: 'group-hover:shadow-indigo-200/50' },
    { from: 'from-green-500', to: 'to-emerald-400', bg: 'bg-green-500', text: 'text-green-600', border: 'border-green-200', hover: 'group-hover:shadow-green-200/50' },
  ];
  return colors[index % colors.length];
};

/* ============================================================
   FEATURED COLLECTIONS DATA
   ============================================================ */
const featuredCollections = [
  {
    name: 'New Arrivals',
    description: 'Check out the latest trends',
    icon: <Sparkles className="w-6 h-6" />,
    from: 'from-amber-400',
    to: 'to-yellow-300',
    link: '/new-arrivals'
  },
  {
    name: 'Best Sellers',
    description: 'Most popular products',
    icon: <TrendingUp className="w-6 h-6" />,
    from: 'from-emerald-400',
    to: 'to-teal-300',
    link: '/products'
  },
  {
    name: 'Top Rated',
    description: 'Highest customer ratings',
    icon: <Star className="w-6 h-6" />,
    from: 'from-orange-400',
    to: 'to-red-300',
    link: '/products'
  },
  {
    name: 'Flash Deals',
    description: 'Limited time offers',
    icon: <Zap className="w-6 h-6" />,
    from: 'from-purple-400',
    to: 'to-pink-300',
    link: '/sale'
  }
];

/* ============================================================
   HERO SECTION
   ============================================================ */
const HeroSection: React.FC<{
  categoriesCount: number;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
}> = ({ categoriesCount, searchQuery, setSearchQuery }) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <section className="relative overflow-hidden bg-slate-900">
      {/* Animated gradient orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ x: [0, 50, 0], y: [0, -20, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 -left-32 w-[400px] h-[400px] rounded-full bg-gradient-to-br from-blue-600/25 to-purple-600/15 blur-3xl"
        />
        <motion.div
          animate={{ x: [0, -30, 0], y: [0, 30, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-gradient-to-tl from-emerald-500/15 to-cyan-500/15 blur-3xl"
        />
      </div>

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '32px 32px'
      }} />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-12 md:py-20">
          {/* Breadcrumb */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-sm text-white/50 mb-6"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-white font-medium">Categories</span>
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mb-4 tracking-tight">
              Explore Our{' '}
              <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-emerald-400 bg-clip-text text-transparent">
                Categories
              </span>
            </h1>
            <p className="text-base md:text-lg text-white/50 mb-8 max-w-2xl leading-relaxed">
              Discover a world of amazing products across {categoriesCount} categories. From electronics to fashion, find everything you need.
            </p>

            {/* Search */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className={`relative max-w-lg transition-all duration-300 ${isFocused ? 'transform scale-[1.01]' : ''}`}
            >
              <div className={`absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-0 transition-opacity duration-300 ${isFocused ? 'opacity-30' : ''}`} />
              <div className="relative flex items-center bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden focus-within:border-white/40 transition-all">
                <Search className="w-5 h-5 text-white/40 ml-5 flex-shrink-0" />
                <input
                  type="text"
                  placeholder="Search categories..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  className="flex-1 bg-transparent border-none outline-none px-4 py-4 text-white placeholder-white/40 text-base"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mr-3 p-2 rounded-full hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap gap-3 mt-8"
          >
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <LayoutGrid className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">{categoriesCount}</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">Categories</div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                <Layers className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">100+</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">Subcategories</div>
              </div>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-xl px-4 py-2.5 flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-sm font-bold text-white">New</div>
                <div className="text-[10px] text-white/40 uppercase tracking-wider">Daily</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50 to-transparent" />
    </section>
  );
};

/* ============================================================
   FEATURED COLLECTIONS
   ============================================================ */
const FeaturedCollections: React.FC = () => {
  return (
    <section className="py-10 md:py-14">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          variants={slideInLeft}
          className="flex items-center gap-3 mb-6"
        >
          <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full" />
          <h2 className="text-xl md:text-2xl font-bold text-gray-900">Curated For You</h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {featuredCollections.map((collection, index) => (
            <motion.div
              key={collection.name}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <Link
                to={collection.link}
                className={`group block relative overflow-hidden rounded-2xl p-5 md:p-6 bg-gradient-to-br ${collection.from} ${collection.to} hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
              >
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-colors" />
                <div className="relative z-10">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-3 shadow-sm">
                    {collection.icon}
                  </div>
                  <h3 className="font-bold text-gray-900 text-sm md:text-base mb-1">{collection.name}</h3>
                  <p className="text-xs text-gray-700 opacity-70">{collection.description}</p>
                </div>
                <ArrowRight className="absolute bottom-4 right-4 w-5 h-5 text-gray-800 opacity-0 group-hover:opacity-100 translate-x-2 group-hover:translate-x-0 transition-all duration-300" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   CATEGORY CARD
   ============================================================ */
const CategoryCard: React.FC<{
  category: Category;
  index: number;
}> = ({ category, index }) => {
  const color = getCategoryColor(index);

  const getCategoryImage = (cat: Category) => {
    if (typeof cat.image === 'object' && cat.image?.url) {
      return cat.image.url;
    }
    if (typeof cat.image === 'string') {
      return cat.image;
    }
    return '';
  };

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-30px' }}
      variants={fadeInUp}
    >
      <Link
        to={`/category/${encodeURIComponent(category.slug || '')}`}
        className={`group relative block overflow-hidden rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 h-full ${color.hover}`}
      >
        {/* Top gradient bar */}
        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${color.from} ${color.to}`} />

        <div className="p-5 md:p-6">
          {/* Icon & Featured badge */}
          <div className="flex items-start justify-between mb-5">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${color.from} ${color.to} flex items-center justify-center text-white shadow-lg shadow-gray-200 group-hover:scale-110 transition-transform duration-300`}>
              {getCategoryIcon(category.icon)}
            </div>
            {category.featured && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold rounded-full shadow-sm">
                <Sparkles className="w-2.5 h-2.5" />
                FEATURED
              </span>
            )}
          </div>

          {/* Title */}
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
            {category.name}
          </h3>

          {/* Description */}
          <p className="text-sm text-gray-500 mb-4 line-clamp-2 leading-relaxed">
            {category.description || `Browse our ${category.name} collection`}
          </p>

          {/* Footer */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Package className="w-3.5 h-3.5" />
              <span className="font-medium">{category.productCount?.toLocaleString() || '0'} products</span>
            </div>
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-blue-50 transition-colors">
              <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
            </div>
          </div>

          {/* Subcategories */}
          {category.subcategories && category.subcategories.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex flex-wrap gap-1.5">
                {category.subcategories.slice(0, 4).map((sub) => (
                  <span
                    key={typeof sub === 'string' ? sub : sub._id}
                    className="px-2.5 py-1 bg-gray-100 text-gray-600 text-[11px] font-medium rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    {typeof sub === 'string' ? sub : sub.name}
                  </span>
                ))}
                {category.subcategories.length > 4 && (
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-400 text-[11px] font-medium rounded-lg">
                    +{category.subcategories.length - 4}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </Link>
    </motion.div>
  );
};

/* ============================================================
   SKELETON & LOADING
   ============================================================ */
const SkeletonCard = () => (
  <div className="rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-100 overflow-hidden animate-pulse">
    <div className="p-5 md:p-6">
      <div className="flex items-start justify-between mb-5">
        <div className="w-14 h-14 rounded-2xl bg-gray-200" />
        <div className="w-16 h-5 rounded-full bg-gray-200" />
      </div>
      <div className="h-6 bg-gray-200 rounded w-2/3 mb-2" />
      <div className="h-4 bg-gray-200 rounded w-full mb-2" />
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
      <div className="flex items-center justify-between">
        <div className="h-4 bg-gray-200 rounded w-20" />
        <div className="w-8 h-8 rounded-full bg-gray-200" />
      </div>
    </div>
  </div>
);

const LoadingState = () => (
  <div className="min-h-screen bg-gray-50">
    {/* Hero Skeleton */}
    <div className="bg-slate-900 py-12 md:py-20">
      <div className="container mx-auto px-4">
        <div className="animate-pulse">
          <div className="h-4 bg-white/10 rounded-full w-48 mb-6" />
          <div className="h-10 bg-white/10 rounded-lg w-72 mb-4" />
          <div className="h-5 bg-white/10 rounded w-96 mb-8" />
          <div className="h-14 bg-white/10 rounded-2xl w-full max-w-lg" />
        </div>
      </div>
    </div>
    <div className="container mx-auto px-4 py-10">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {Array.from({ length: 9 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    </div>
  </div>
);

/* ============================================================
   EMPTY STATE
   ============================================================ */
const EmptyState: React.FC<{ searchQuery: string; onClear: () => void }> = ({ searchQuery, onClear }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.4 }}
    className="flex flex-col items-center justify-center py-20 text-center"
  >
    <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-gray-200 flex items-center justify-center mb-6">
      <Search className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-2xl font-bold text-gray-900 mb-2">No categories found</h3>
    <p className="text-gray-500 mb-6 max-w-md">
      We couldn't find any categories matching &quot;<span className="font-semibold text-gray-700">{searchQuery}</span>&quot;.
    </p>
    <button
      onClick={onClear}
      className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
    >
      <X className="w-4 h-4" />
      Clear Search
    </button>
  </motion.div>
);

/* ============================================================
   ERROR STATE
   ============================================================ */
const ErrorState: React.FC<{ error: string; onRetry: () => void }> = ({ error, onRetry }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="text-center max-w-md"
    >
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center mx-auto mb-6 border border-red-100">
        <Loader2 className="w-10 h-10 text-red-400" />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Failed to load categories</h2>
      <p className="text-gray-500 mb-8">{error}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-2 bg-slate-900 text-white px-6 py-3 rounded-xl font-medium hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
      >
        <Loader2 className="w-4 h-4" />
        Try Again
      </button>
    </motion.div>
  </div>
);

/* ============================================================
   BOTTOM CTA
   ============================================================ */
const BottomCTA: React.FC<{ categoriesCount: number }> = ({ categoriesCount }) => (
  <section className="py-12 md:py-16 border-t border-gray-200/80">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-50px' }}
        variants={scaleIn}
        className="relative overflow-hidden bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-3xl p-8 md:p-12 text-center"
      >
        {/* Decorative dots */}
        <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
          <div className="absolute top-8 right-8 w-3 h-3 rounded-full bg-white" />
          <div className="absolute top-16 right-24 w-2 h-2 rounded-full bg-white" />
          <div className="absolute top-24 right-12 w-4 h-4 rounded-full bg-white" />
          <div className="absolute top-32 right-32 w-2 h-2 rounded-full bg-white" />
        </div>
        <div className="absolute bottom-0 left-0 w-64 h-64 opacity-10">
          <div className="absolute bottom-8 left-8 w-2 h-2 rounded-full bg-white" />
          <div className="absolute bottom-16 left-24 w-3 h-3 rounded-full bg-white" />
          <div className="absolute bottom-24 left-12 w-2 h-2 rounded-full bg-white" />
        </div>

        <div className="relative z-10">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-500/30">
            <ShoppingBag className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-4">
            Start Shopping Today!
          </h2>
          <p className="text-white/50 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
            Explore thousands of products across {categoriesCount} categories. Great deals, fast shipping, and excellent customer service.
          </p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 transition-colors shadow-lg text-base md:text-lg"
          >
            <ShoppingBag className="w-5 h-5" />
            Browse All Products
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============================================================
   MAIN COMPONENT
   ============================================================ */
const CategoriesPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await categoryService.getCategories();

      let categoriesData: Category[] = [];
      if (Array.isArray(response)) {
        categoriesData = response;
      } else if (response && typeof response === 'object' && 'data' in response) {
        categoriesData = (response as any).data || [];
      } else if (response && typeof response === 'object' && 'categories' in response) {
        categoriesData = (response as any).categories || [];
      }

      setCategories(categoriesData);
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError(err?.message || 'Failed to load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const filteredCategories = useMemo(() => {
    if (!Array.isArray(categories)) return [];
    if (!searchQuery.trim()) return categories;
    const query = searchQuery.toLowerCase();
    return categories.filter(cat =>
      cat.name.toLowerCase().includes(query) ||
      (cat.description && cat.description.toLowerCase().includes(query))
    );
  }, [categories, searchQuery]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState error={error} onRetry={fetchCategories} />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <HeroSection
        categoriesCount={categories.length}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />

      {/* Featured Collections */}
      <FeaturedCollections />

      {/* Categories Grid */}
      <section className="pb-10 md:pb-14">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={slideInLeft}
            className="flex items-center justify-between mb-6"
          >
            <div className="flex items-center gap-3">
              <div className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-cyan-500 rounded-full" />
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">All Categories</h2>
              <span className="text-sm text-gray-400 hidden sm:inline">{filteredCategories.length} found</span>
            </div>
          </motion.div>

          {filteredCategories.length === 0 ? (
            <EmptyState searchQuery={searchQuery} onClear={() => setSearchQuery('')} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {filteredCategories.map((category, index) => (
                <CategoryCard
                  key={category._id}
                  category={category}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <BottomCTA categoriesCount={categories.length} />
    </div>
  );
};

export default CategoriesPage;
